/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2019 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import {BlockNavigationOptions, LocationLike, PageApi, ShellRedirectOptions} from '@adobe/exc-app/page';
import {engine} from '../runtimeDeferred';
import {Events} from '@exc/shared';
import {ExternalConfiguration} from '../ExternalConfiguration';
import {generateShellUrl} from '../util';
import {messenger} from '../Messenger';
import {setupConfig} from '../config';

let configuration: ExternalConfiguration;

export const OBSERVER_DEFAULT_SELECTORS = ['[role="dialog"]'];
let observerSelectors: Array<string> = OBSERVER_DEFAULT_SELECTORS;

export function addTakeover(): void {
  if (document.getElementById('shell-placeholder')) {
    return;
  }
  const div = document.createElement('div');
  div.id = 'shell-placeholder';
  document.body.insertBefore(div, document.body.childNodes[0]);
  messenger.send(Events.CAN_TAKEOVER, {});
}

/**
 * Callback used by modalAutoDetect's mutation observer.
 * @param mutation A MutationRecord from a mutaton observer
 * @param enable If true, checks for addedNodes and sets page.modal = true,
 * if false, checks for removedNodes and set page.modal = false.
 */
export function modalSetter(mutation: MutationRecord, enable: boolean) {
  mutation[enable ? 'addedNodes' : 'removedNodes'].forEach(node => {
    if (node.nodeType !== Node.ELEMENT_NODE) {
      return;
    }

    const allSelectors = page.getModalQuerySelectors().join(',');
    const el = (node as Element);
    let allModalMatches = el.querySelectorAll(allSelectors);
    if (allModalMatches.length > 0 || el.matches(allSelectors)) {
      page.modal = enable;
    }
  });
}

// Setup the mutation observer to use with modalAutoDetect when turned on
export const observer = new MutationObserver(function (mutationsList: MutationRecord[]) {
  for (const mutation of mutationsList) {
    modalSetter(mutation, true);
    modalSetter(mutation, false);
  }
});

const page = {
  blockNavigation: (enabled: boolean, options?: BlockNavigationOptions): void => {
    messenger.send(Events.BLOCK_NAVIGATION, {enabled, options});
  },
  clipboardWrite: (msg: string): void => {
    messenger.send(Events.CLIPBOARD_WRITE, {msg});
  },
  done: () => {
    engine.done(performance.now());
  },
  generateShellUrl: (location: LocationLike, newApp?: boolean) => {
    return generateShellUrl(configuration, location, newApp);
  },
  getModalQuerySelectors: (): Array<string> => observerSelectors && observerSelectors.length ? observerSelectors : OBSERVER_DEFAULT_SELECTORS,
  iframeReload: (cacheBust?: boolean): void => {
    cacheBust = cacheBust === undefined ? !configuration.spaAppId : cacheBust;
    messenger.send(Events.IFRAME_RELOAD, {cacheBust});
    cacheBust || window.location.reload();
  },
  notFound: (): void => {
    messenger.send(Events.NOT_FOUND, {});
  },
  openInNewTab: (path: string, newApp?: boolean) => {
    window.open(page.generateShellUrl({path}, newApp));
  },
  setModalQuerySelectors: (selectors: Array<string>): void => {
    observerSelectors = selectors;
  },
  shellRedirect: (path, options?: ShellRedirectOptions) => messenger.send(Events.SHELL_REDIRECT, {...options, path})
} as PageApi;

export function onConfigure(config: ExternalConfiguration): void {
  configuration = config;
}

setupConfig(
  page,
  [
    'appContainer',
    'favicon',
    'modal',
    'modalAutoDetect',
    'preventDefaultCombos',
    'spinner',
    'title',
    'unloadPromptMessage'
  ],
  (apiName, value) => {
    if (value && apiName === 'appContainer') {
      addTakeover();
    }

    if (apiName === 'modalAutoDetect') {
      if (value) {
        observer.observe(document.body, {childList: true, subtree: true});
      } else {
        observer.disconnect();
      }
    }
  });

export default page;
